import { useMediaQuery } from '@mantine/hooks';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from 'theme/components/utils/tablet';

export type TableActionMode = 'desktop' | 'tablet' | 'mobile';

interface ResponsiveDesignContextValue {
  isTablet: boolean;
  isMobile: boolean;
  tableActionMode: TableActionMode;
}

const ResponsiveDesignContext = createContext<ResponsiveDesignContextValue>({
  isTablet: false,
  isMobile: false,
  tableActionMode: 'desktop',
});

/**
 * Provides responsive design breakpoint flags to the component tree.
 * Mount once near the root to avoid redundant media-query listeners.
 */
export function ResponsiveDesignProvider({ children }: { children: ReactNode }) {
  const isTablet = useMediaQuery(`(max-width: ${TABLET_BREAKPOINT - 1}px)`, false, {
    getInitialValueInEffect: false,
  });
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`, false, {
    getInitialValueInEffect: false,
  });

  const value = useMemo(() => {
    const tablet = isTablet ?? false;
    const mobile = isMobile ?? false;
    const tableActionMode: TableActionMode = mobile ? 'mobile' : tablet ? 'tablet' : 'desktop';

    return { isTablet: tablet, isMobile: mobile, tableActionMode };
  }, [isMobile, isTablet]);

  return <ResponsiveDesignContext.Provider value={value}>{children}</ResponsiveDesignContext.Provider>;
}

/**
 * Returns responsive design breakpoint flags.
 *
 * isTablet: true when screen width is ≤ 1024px.
 * isMobile: true when screen width is ≤ 768px.
 */
export function useResponsiveDesign() {
  return useContext(ResponsiveDesignContext);
}

/**
 * Width for the _actions column on tablet — fits a primary ActionIcon and the menu-trigger icon.
 */
export const TABLET_ACTIONS_COLUMN_WIDTH = 100;

/**
 * Width for the _actions column on mobile — fits only the menu-trigger icon.
 */
export const MOBILE_ACTIONS_COLUMN_WIDTH = 32;

/** Smallest desktop width that fits every localized HR primary-action label and icon. */
export const SINGLE_ACTION_BUTTON_WIDTH = 120;

/** Desktop actions-column width for the fixed primary button, menu trigger, gap, and cell padding. */
export const SINGLE_ACTIONS_COLUMN_WIDTH = 196;

/**
 * Resolves the responsive width of the pinned actions column.
 */
export function getResponsiveActionsColumnWidth(desktopWidth: number, tableActionMode: TableActionMode) {
  if (tableActionMode === 'mobile') {
    return MOBILE_ACTIONS_COLUMN_WIDTH;
  }

  if (tableActionMode === 'tablet') {
    return TABLET_ACTIONS_COLUMN_WIDTH;
  }

  return desktopWidth;
}
